import { type Listener, type Message, type MessageConsumer } from './types';

export type CreateMessageConsumerOptions = {
  allowedTargetOrigins?: string[];
  originWindow?: Window;
};

export const createMessageConsumer = <OriginMessage extends Message<string>>(
  { allowedTargetOrigins, originWindow = window }: CreateMessageConsumerOptions = { originWindow: window }
): MessageConsumer<OriginMessage> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const listeners: Record<string, Listener<any>[]> = {};

  const onMessageEventListener = (event: MessageEvent) => {
    if (allowedTargetOrigins && !allowedTargetOrigins.includes(event.origin)) {
      return;
    }
    const message = event.data as OriginMessage;
    const messageListeners = listeners[message.type] || [];

    if (typeof message === 'object' && 'payload' in message) {
      messageListeners.forEach((listener) => (listener as Listener<typeof message.payload>)(message.payload));
    } else {
      messageListeners.forEach((listener) => (listener as Listener<undefined>)(undefined));
    }
  };

  originWindow.addEventListener('message', onMessageEventListener);

  const consumer: MessageConsumer<OriginMessage> = {
    addListener: (type, listener) => {
      listeners[type] = [...(listeners[type] || []), listener];
    },
    destroy: () => {
      originWindow.removeEventListener('message', onMessageEventListener);
    },
    removeListener: (type, listener) => {
      listeners[type] = (listeners[type] || []).filter((existingListener) => existingListener !== listener);
    },
    addAsyncListener: <T extends OriginMessage['type']>(type: T) => {
      return new Promise<Extract<OriginMessage, { type: T }> extends { payload: infer P } ? P : undefined>(
        (resolve) => {
          const listener = ((
            payload: Extract<OriginMessage, { type: T }> extends { payload: infer P } ? P : undefined
          ) => {
            consumer.removeListener(type, listener);
            resolve(payload);
          }) as Extract<OriginMessage, { type: T }> extends { payload: infer P } ? Listener<P> : Listener<undefined>;

          consumer.addListener(type, listener);
        }
      );
    },
  };

  return consumer;
};
